// Default StashUpdatesSource implementation — Server-Sent Events transport.
//
// Bulk load goes through the same-origin REST endpoint with the project's
// existing axios + getAuthHeaders flow. The live stream uses the browser's
// native EventSource, which handles Last-Event-ID resume automatically.

import axios from 'axios';
import { getAuthHeaders } from '../../../services/firebase';
import type { ApiResponse, StashScopedSnapshot } from '@stash/shared-types';
import type {
  InitialSnapshot,
  LoadInitialArgs,
  StashUpdatesSource,
  SubscribeArgs,
} from './StashUpdatesSource';

const SNAPSHOT_URL = '/api/stash-scoped/snapshot';
const STREAM_URL = '/api/stream/stash-updates';

export class SseStashUpdatesSource implements StashUpdatesSource {
  async loadInitial(args: LoadInitialArgs = {}): Promise<InitialSnapshot> {
    const headers = await getAuthHeaders();
    const params: Record<string, string> = {};
    if (args.vendorIds && args.vendorIds.length > 0) {
      params.vendorIds = args.vendorIds.join(',');
    }
    const response = await axios.get<ApiResponse<StashScopedSnapshot>>(SNAPSHOT_URL, {
      headers,
      params,
    });
    const data = response.data.data;
    console.debug('Loaded initial snapshot', data);
    return {
      cards: data.cards,
      vendors: data.vendors,
      stashers: data.stashers,
      categories: data.categories,
      cursor: data.cursor,
      activeVendorIds: data.activeVendorIds,
    };
  }

  subscribe(args: SubscribeArgs): () => void {
    const { cursor, vendorIds, onChanges, onStatus, onError } = args;
    const url = new URL(STREAM_URL, window.location.origin);
    url.searchParams.set('cursor', cursor);
    if (vendorIds && vendorIds.length > 0) {
      url.searchParams.set('vendorIds', vendorIds.join(','));
    }

    onStatus?.('connecting');
    const eventSource = new EventSource(url.toString());

    eventSource.onopen = (): void => {
      onStatus?.('open');
    };

    eventSource.onerror = (err): void => {
      // EventSource auto-reconnects on transient errors; surface the state
      // change for any UI banner the consumer wants to render.
      onError?.(err);
      onStatus?.('reconnecting');
    };

    eventSource.addEventListener('changes', (event) => {
      try {
        const payload = JSON.parse((event as MessageEvent).data);
        onChanges(payload.changes, payload.cursor);
      } catch (parseErr) {
        onError?.(parseErr);
      }
    });

    eventSource.addEventListener('bye', () => {
      // Server-initiated graceful close at ~55s. The browser's EventSource
      // auto-reconnects with Last-Event-ID — no manual action needed beyond
      // status signalling.
      onStatus?.('reconnecting');
    });

    return (): void => {
      eventSource.close();
      onStatus?.('closed');
    };
  }
}
